import React, { useState, useContext } from 'react';
import { Box, Typography, Divider, CircularProgress, TextField, InputAdornment, Collapse } from '@material-ui/core';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentRoute } from '../../store/slices/routesSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { selectFuelPrices, selectFuelPriceOverrides, selectFuelPricesLoading, setFuelPriceOverride } from '../../store/slices/fuelPricesSlice';
import { t } from '../../i18n';
import SectionTitle from '../common/SectionTitle';
import GlassCard from '../common/GlassCard';
import { FUEL_TYPES } from '../Sidebar/VehicleDriverForm';
import { resolveFuelPriceEUR } from '../../utils/resolveFuelPrice';
import { AuthContext } from '../../App';

// Country flag emoji from ISO 3166-1 alpha-3
function countryFlag(code3) {
  const MAP_3_TO_2 = {
    PRT: 'PT', ESP: 'ES', FRA: 'FR', DEU: 'DE', ITA: 'IT',
    BEL: 'BE', NLD: 'NL', LUX: 'LU', AUT: 'AT', CHE: 'CH',
    GBR: 'GB', IRL: 'IE', DNK: 'DK', SWE: 'SE', NOR: 'NO',
    FIN: 'FI', POL: 'PL', CZE: 'CZ', HUN: 'HU', SVK: 'SK',
    SVN: 'SI', HRV: 'HR', ROU: 'RO', BGR: 'BG', GRC: 'GR',
    AGO: 'AO', NAM: 'NA', ZAF: 'ZA', MOZ: 'MZ',
  };
  const code2 = MAP_3_TO_2[code3] || code3?.slice(0, 2);
  if (!code2 || code2.length < 2) return '';
  return String.fromCodePoint(
    ...code2.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
}

const COUNTRY_NAMES = {
  PRT: 'Portugal', ESP: 'Espanha', FRA: 'França', DEU: 'Alemanha', ITA: 'Itália',
  BEL: 'Bélgica', NLD: 'Países Baixos', LUX: 'Luxemburgo', AUT: 'Áustria',
  CHE: 'Suíça', GBR: 'Reino Unido', IRL: 'Irlanda', DNK: 'Dinamarca',
  SWE: 'Suécia', NOR: 'Noruega', FIN: 'Finlândia', POL: 'Polónia',
  CZE: 'Chéquia', HUN: 'Hungria', SVK: 'Eslováquia', SVN: 'Eslovénia',
  HRV: 'Croácia', ROU: 'Roménia', BGR: 'Bulgária', GRC: 'Grécia',
  AGO: 'Angola', NAM: 'Namíbia', ZAF: 'África do Sul', MOZ: 'Moçambique',
};

function FuelCountryPanel({ lang, currency: routeCurrency, tripMultiplier = 1 }) {
  const dispatch = useDispatch();
  const route = useSelector(selectCurrentRoute);
  const vehicles = useSelector(selectVehicles);
  const vehicle = vehicles.find(v => v.id === route.vehicleId);
  const fuelPrices = useSelector(selectFuelPrices);
  const overrides = useSelector(selectFuelPriceOverrides);
  const loading = useSelector(selectFuelPricesLoading);
  const { clientSettings } = useContext(AuthContext);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [expanded, setExpanded] = useState(false);

  const cr = route.computedRoute;
  const countryDistances = cr?.countryDistances;

  // If no per-country data, don't render (fallback to original panel)
  if (!countryDistances || Object.keys(countryDistances).length === 0) return null;

  const fuelType = route.fuelType || 'diesel';
  const fuelDef = FUEL_TYPES.find(f => f.value === fuelType) || FUEL_TYPES[0];
  const vehicleType = route.routeOptions?.vehicleType || 'hgv';
  // Car consumption is roughly 35-40% of truck consumption
  const defaultConsumption = vehicleType === 'car'
    ? Math.round(fuelDef.consumptionDefault * 0.38)
    : fuelDef.consumptionDefault;
  const consumption = route.fuelConsumption ?? vehicle?.fuelPer100km ?? defaultConsumption;

  const cur = routeCurrency?.symbol || '€';
  const rate = routeCurrency?.rate || 1;
  const conv = (eur) => {
    const val = eur * rate;
    const dp = rate >= 100 ? 0 : 2;
    return val.toLocaleString('pt-PT', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  };

  // Sort countries by distance (largest first)
  const countries = Object.entries(countryDistances)
    .sort((a, b) => b[1] - a[1]);

  // Sidebar's `route.fuelPrice` só faz sentido como override global
  // quando a rota toca um único país — em rotas multi-país (UE típicas)
  // ignoramos para deixar cada país usar EU Oil Bulletin ou Preferências.
  const singleCountry = countries.length === 1;

  function getPrice(cc) {
    return resolveFuelPriceEUR(cc, fuelType, {
      overrides, clientSettings, route, fuelPrices,
      routeRate: rate, fuelDef, singleCountry,
    });
  }

  // Calculate totals
  let totalCostEUR = 0;
  let totalFuel = 0;
  const countryRows = countries.map(([cc, distM]) => {
    const distKm = (distM / 1000) * tripMultiplier;
    const fuel = (distKm / 100) * consumption;
    const pricePerUnit = getPrice(cc);
    const costEUR = fuel * pricePerUnit;
    totalCostEUR += costEUR;
    totalFuel += fuel;
    return { cc, distKm, fuel, pricePerUnit, costEUR, isOverride: overrides[cc] != null };
  });

  const co2Kg = fuelDef.co2PerUnit > 0 ? totalFuel * fuelDef.co2PerUnit : 0;

  function handleStartEdit(cc, currentPrice) {
    setEditingCountry(cc);
    setEditValue(String(currentPrice));
  }

  function handleSaveEdit(cc) {
    const val = parseFloat(editValue.replace(',', '.'));
    if (!isNaN(val) && val > 0) {
      dispatch(setFuelPriceOverride({ countryCode: cc, price: val }));
    }
    setEditingCountry(null);
  }

  if (loading) {
    return (
      <Box>
        <SectionTitle icon={LocalGasStationIcon}>{t(lang, 'fuelByCountry')}</SectionTitle>
        <GlassCard flat padding="sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CircularProgress size={14} />
          <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            {t(lang, 'loadingFuelPrices')}
          </Typography>
        </GlassCard>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header colapsável — mesmo padrão do "Trajeto completo".
          Mostra sempre o total (mais importante) e esconde o breakdown
          por país por defeito para não poluir o painel quando a rota
          atravessa muitos países. */}
      <Box
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(v => !v)}
      >
        <SectionTitle icon={LocalGasStationIcon}>{t(lang, 'fuelByCountry')}</SectionTitle>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
            {countryRows.length} {countryRows.length === 1 ? (lang === 'pt' ? 'país' : 'country') : (lang === 'pt' ? 'países' : 'countries')}
          </Typography>
          {expanded ? <ExpandLessIcon style={{ fontSize: 18, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 18, color: '#94a3b8' }} />}
        </Box>
      </Box>

      <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* Total cost — sempre visível, é o resumo essencial */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t(lang, 'totalEstimatedCost')}
          </Typography>
          <Typography style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b' }}>
            {conv(totalCostEUR)} {cur}
          </Typography>
        </Box>

        <Collapse in={expanded}>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

        <Divider style={{ margin: '2px 0' }} />

        {/* Per-country breakdown */}
        {countryRows.map(({ cc, distKm, fuel, pricePerUnit, costEUR, isOverride }) => (
          <Box key={cc} style={{ padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
            {/* Country header */}
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.85rem' }}>{countryFlag(cc)}</span>
                <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 600 }}>
                  {COUNTRY_NAMES[cc] || cc}
                </Typography>
                <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                  {distKm.toFixed(0)} km
                </Typography>
              </Box>
              <Typography style={{ fontSize: '0.78rem', color: '#f59e0b', fontWeight: 600 }}>
                {conv(costEUR)} {cur}
              </Typography>
            </Box>

            {/* Price per unit (editable) */}
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 28 }}>
              <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                {fuel.toFixed(1)} {fuelDef.unit} ×
              </Typography>

              {editingCountry === cc ? (
                <TextField
                  size="small"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => handleSaveEdit(cc)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(cc); if (e.key === 'Escape') setEditingCountry(null); }}
                  autoFocus
                  InputProps={{
                    endAdornment: <InputAdornment position="end" style={{ fontSize: '0.6rem' }}>€/{fuelDef.unit}</InputAdornment>,
                    style: { fontSize: '0.68rem', height: 24, width: 100 },
                  }}
                  style={{ margin: 0 }}
                />
              ) : (
                <Box
                  onClick={() => handleStartEdit(cc, pricePerUnit)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                    padding: '1px 6px', borderRadius: 4,
                    background: isOverride ? 'rgba(59,130,246,0.08)' : 'transparent',
                    border: `1px solid ${isOverride ? 'rgba(59,130,246,0.3)' : 'transparent'}`,
                    transition: 'all 0.15s',
                  }}
                  title={t(lang, 'editPrice')}
                >
                  <Typography style={{
                    fontSize: '0.68rem', fontWeight: 500,
                    color: isOverride ? '#3b82f6' : '#0f172a',
                  }}>
                    {pricePerUnit.toFixed(3)} €/{fuelDef.unit}
                  </Typography>
                  <EditIcon style={{ fontSize: 10, color: '#94a3b8', opacity: 0.6 }} />
                </Box>
              )}
            </Box>
          </Box>
        ))}

        {/* Summary row */}
        <Divider style={{ margin: '2px 0' }} />
        <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{t(lang, 'consumptionLbl')}</Typography>
          <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 500 }}>
            {totalFuel.toFixed(1)} {fuelDef.unit}
          </Typography>
        </Box>
        {co2Kg > 0 && (
          <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{t(lang, 'co2Emissions')}</Typography>
            <Typography style={{ fontSize: '0.72rem', color: '#22c55e', fontWeight: 500 }}>
              {co2Kg >= 1000 ? `${(co2Kg / 1000).toFixed(2)} t` : `${co2Kg.toFixed(0)} kg`}
            </Typography>
          </Box>
        )}

        {/* Source hint */}
        <Typography style={{ fontSize: '0.55rem', color: '#cbd5e1', fontStyle: 'italic', marginTop: 2 }}>
          {t(lang, 'fuelPricesSource')} · {t(lang, 'editPrice')}
        </Typography>
          </Box>
        </Collapse>
      </GlassCard>
    </Box>
  );
}

export default FuelCountryPanel;
